import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {ToggleSwitch, ToggleSwitchV2Size} from '@Components/toggle-switch-v2';
import {
  useEventWizardIsRemoveWatermark,
  useEventWizardIsSharedAsGoogleEvent,
  useEventWizardIsSharedInTeam,
  useEventWizardTeamName,
} from '@Components/event-wizard/event-wizard.hooks';
import {useDispatch} from 'react-redux';
import {onEventWizardRemoveWatermarkToggle, onEventWizardShareAsGoogleEventToggle, onEventWizardShareInTeamToggle} from '@Components/event-wizard/event-wizard-slice';
import {updatePreviewWatermark} from '@Components/event-wizard/preview/preview.helper';
import {EventWizardPremiumIcon} from '@Components/event-wizard/event-wizard-premium-icon';
import styles from './event-wizard-other-options.module.scss';
import {shouldUpsellUserForRemoveWatermark, shouldUpsellUserForTeamShare} from '@Components/event-wizard/event-wizard-other-options/event-wizard-other-options.types';
import {EventUpsellType, openEventsUpsellDialog} from '@Libraries/events.library';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import {isPAYGUser, isPremiumUser} from '@Utils/user.util';

export function EventWizardOtherOptions(): ReactElement<any> {
  const dispatch = useDispatch();

  const isRemoveWatermark = useEventWizardIsRemoveWatermark();
  const isShareToggled = useEventWizardIsSharedInTeam();
  const teamName = useEventWizardTeamName();
  const isSharedAsGoogleEvent = useEventWizardIsSharedAsGoogleEvent();

  const toggleShareAsGoogleEvent = (): void => {
    dispatch(onEventWizardShareAsGoogleEventToggle(!isSharedAsGoogleEvent));
  };

  const toggleShare = (): void => {
    if (shouldUpsellUserForTeamShare()) {
        openEventsUpsellDialog(EventUpsellType.SHARE_TEAM, undefined);
      return;
    }
    dispatch(onEventWizardShareInTeamToggle(!isShareToggled));
  };

  const toggleRemoveWatermark = (): void => {
    if (shouldUpsellUserForRemoveWatermark()) {
        openEventsUpsellDialog(EventUpsellType.REMOVE_WATERMARK, undefined);
      return;
    }
    dispatch(onEventWizardRemoveWatermarkToggle(!isRemoveWatermark));
    updatePreviewWatermark(!isRemoveWatermark, true);
  };

  const getTeamOptionText = (): string => {
    const calendarURL = window.PMW.util.site_url('posters/mine#/events-calendar');
    if (teamName && isPremiumUser()) {
      return window.i18next.t('pmwjs_add_to_team_and_event_calendar', {teamURL: window.PMW.util.site_url('posters/mine#/events-shared'), calendarURL: calendarURL});
    }

    return isPAYGUser() ? window.i18next.t('pmwjs_add_to_team'):  window.i18next.t('pmwjs_add_to_event_calendar', {calendarURL: calendarURL});
  };

  const getGoogleShareTooltip = (): ReactElement<any> => {
    return (
      <OverlayTrigger
        overlay={<Tooltip className={`${styles.tooltipWidth} spacing-m-b-2`}>{window.i18next.t('pmwjs_share_as_google_event_tooltip')}</Tooltip>}
        placement="top"
        trigger={['focus', 'hover']}
      >
        <div className="flexbox spacing-m-l-1">
          <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} type={IconType.GHOST} shape={IconShape.SQUARE} />
        </div>
      </OverlayTrigger>
    );
  };

  return (
    <div className="flex-column-align-start">
      <div className="flex-row-align-center justify-content-between spacing-m-b-4 _full-width">
        <Text val={getTeamOptionText()} size={TextSize.XSMALL} dangerouslySetInnerHTML/>
        {shouldUpsellUserForTeamShare() ? <EventWizardPremiumIcon upsellType={EventUpsellType.SHARE_TEAM} /> : undefined}
        <ToggleSwitch className={styles.marginLeftAuto} isChecked={isShareToggled} onChange={toggleShare} size={ToggleSwitchV2Size.SMALL} />
      </div>
      <div className="flex-row-align-center spacing-m-b-4 _full-width">
        <Text val={window.i18next.t('pmwjs_remove_postermywall_branding')} size={TextSize.XSMALL} />
        {shouldUpsellUserForRemoveWatermark() ? <EventWizardPremiumIcon upsellType={EventUpsellType.REMOVE_WATERMARK} /> : undefined}
        <ToggleSwitch className={styles.marginLeftAuto} isChecked={isRemoveWatermark} onChange={toggleRemoveWatermark} size={ToggleSwitchV2Size.SMALL} />
      </div>
      <div className={`${styles.tooltipRow} flex-row-align-center justify-content-between _full-width`}>
        <Text val={window.i18next.t('pmwjs_share_as_google_event')} size={TextSize.XSMALL} />
        {getGoogleShareTooltip()}
        <ToggleSwitch className={styles.marginLeftAuto} isChecked={isSharedAsGoogleEvent} onChange={toggleShareAsGoogleEvent} size={ToggleSwitchV2Size.SMALL} />
      </div>
    </div>
  );
}
